import { Chip } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { getPermissionData, getUserData } from "../../Actions/common";
import { getTermsAndConditionsByIdAction, listTermsAndConditionsAction, publishTermsAndConditions, } from "../../Actions/termsAndConditionsAction";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import AddEditTermsConditionModal from "./AddEditTermsConditionModal";
import TermsAndConditionPreviewModal from "./TermsAndConditionPreviewModal";
import renderStatusBadge from "../../utils/badges";
import showConfirmation from "../../utils/Conformation";
import DataTable from "../../components/DataTable/DataTable";

const TermsAndConditionsSection = () => {
  const [searchValue, setSearchValue] = useState("");
  const [templateTypeFilter, setTemplateTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isRequiredFilter, setIsRequiredFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldFetchData, setShouldFetchData] = useState(1);
  const [ipDataById, setIpDataById] = useState(null);
  const [whiteListIpsData, setWhiteListIpsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  const [openModal, setOpenModal] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "terms-and-conditions"
  );

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

   const getWhiteListIps = useCallback(async () => {
    setLoading(true);
    try {
      const filterData = {
        page: currentPage,
        perPage: selectedRowsPerPage,
        searchQuery: searchValue || undefined,  // Ensure empty strings are not sent
        templateType: templateTypeFilter !== "all" ? templateTypeFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        isRequired: isRequiredFilter !== "all" ? isRequiredFilter : undefined,
      };

      const response = await listTermsAndConditionsAction(filterData);
      
      setWhiteListIpsData(response?.data || []);
      setTotalCount(response?.totalCount || 0);
    } catch (error) {
      console.error('Error in getWhiteListIps:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to fetch data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedRowsPerPage, searchValue, templateTypeFilter, statusFilter, isRequiredFilter]);


  
  useEffect(() => {
    if (shouldFetchData) {
      getWhiteListIps();
      setShouldFetchData(0);
    }
  }, [shouldFetchData, getWhiteListIps]);

  const handleAddTemplate = async (e, row) => {
    if (row && row?.id) {
      await getTermsAndConditionsByIdAction(row?.id)
        .then((res) => {
          setIpDataById(res.data);
          setOpenModal(true);
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: error?.response?.data?.message,
            severity: "error",
          });
        });
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIpDataById(null);
  };

  const handlePreview = (e, row) => {
    e.stopPropagation();
    setPreviewContent(row);
    setIsPreviewOpen(true);
  };

  const handleClosePreviewModal = () => {
    setIsPreviewOpen(false);
    setPreviewContent(null);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    await showConfirmation("toggleStatus", {
      entityName: "Template",
      entityId: id,
      currentStatus: currentStatus,
      action: async () => {
        const response = await publishTermsAndConditions({
          id,
          isActive: !currentStatus
        });

        if (response?.status !== 200 && !response?.success) {
          throw new Error(response?.message || "Failed to update template status.");
        }

        return response;
      },
      onSuccess: (response, newStatus) => {
        setSnackbar({
          open: true,
          message: response?.message || `Template ${newStatus ? "activated" : "deactivated"} successfully.`,
          severity: "success"
        });
        setShouldFetchData(1);
      },
      onError: (err) => {
        console.error("Error updating status:", err);
        setSnackbar({
          open: true,
          message: err?.response?.data?.message || err?.message || "Something went wrong while updating status.",
          severity: "error"
        });
      },
    });
  };

  const columns = [
    {
      field: "title",
      headerName: "Name",
      minWidth: 250,
    },
    {
      field: "templateType",
      headerName: "Template Type",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { isMaster, isTenant } = row;

        let label = "Unknown";
        let chipColor = "default";
        let borderColor = "grey.500";
        let bgColor = "transparent";

        if (isMaster && isTenant) {
          label = "Both";
          chipColor = "default";
          borderColor = "grey.500";
          bgColor = "transparent";
        } else if (isMaster) {
          label = "Master";
          chipColor = "primary.main";
          borderColor = "primary.main";
        } else if (isTenant) {
          label = "Tenant";
          chipColor = "secondary.main";
          borderColor = "secondary.main";
        }

        return (
          <Chip
            label={label}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "capitalize",
              color: chipColor,
              borderColor: borderColor,
              backgroundColor: bgColor,
            }}
          />
        );
      },
    },
    {
      field: "isRequired",
      headerName: "Required",
      minWidth: 140,
      renderCell: ({ row }) => {
        const statusMap = {
          true: { text: "True", status: "primary" },
          false: { text: "False", status: "default" },
        };

        const { text, status } = statusMap[row?.isRequired] || {
          text: "Unknown",
          status: "default",
        };

        return renderStatusBadge({
          status,
          title: text,
        });
      },
    },
    {
      field: "isActive",
      headerName: "Status",
      minWidth: 140,
      renderCell: ({ row }) => {
        const statusMap = {
          true: { text: "Active", status: "success" },
          false: { text: "Inactive", status: "error" },
        };

        const { text, status } = statusMap[row?.isActive] || {
          text: "Unknown",
          status: "default",
        };

        return renderStatusBadge({
          status,
          title: text,
        });
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      minWidth: 180,
      renderCell: ({ row }) =>
        formatDateTime(row.createdAt, true),
    },
  ];

  const filters = [
    {
      key: "search",
      name: "search",
      type: "search",
      placeholder: "Search by title",
    },
    {
      key: "templateType",
      name: "templateType",
      label: "Template Type",
      type: "select",
      options: [
        { label: "All Templates", value: "all" },
        { label: "Master", value: "master" },
        { label: "Tenant", value: "tenant" },
        { label: "Both", value: "both" },
      ],
    },
    {
      key: "status",
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "All Status", value: "all" },
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
    },
    {
      key: "isRequired",
      name: "isRequired",
      label: "Required",
      type: "select",
      options: [
        { label: "All", value: "all" },
        { label: "Required", value: "true" },
        { label: "Not Required", value: "false" },
      ],
    },
  ];

  const filterValues = {
    search: searchValue,
    templateType: templateTypeFilter,
    status: statusFilter,
    isRequired: isRequiredFilter,
  };

  const handleFilterApply = (values) => {
    setSearchValue(values.search || "");
    setTemplateTypeFilter(values.templateType || "all");
    setStatusFilter(values.status || "all");
    setIsRequiredFilter(values.isRequired || "all");

    setCurrentPage(1);
    setShouldFetchData(1);
  };

  const handleFilterReset = () => {
    setSearchValue("");
    setTemplateTypeFilter("all");
    setStatusFilter("all");
    setIsRequiredFilter("all");

    setCurrentPage(1);
    setShouldFetchData(1);
  };

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <DataTable
        columns={columns}
        rows={whiteListIpsData}
        loading={loading}
        totalCount={totalCount}
        page={currentPage}
        pageSize={selectedRowsPerPage}

        filters={filters}
        filterValues={filterValues}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}

        onPageChange={(page) => {
          setCurrentPage(page);
          setShouldFetchData(1);
        }}

        onPageSizeChange={(size) => {
          setSelectedRowsPerPage(size);
          setCurrentPage(1);
          setShouldFetchData(1);
        }}

        hasCreatePermission={hasPermission?.canCreate}
        addButtonText="ADD"
        onAddClick={handleAddTemplate}

        actions={{
          edit: hasPermission?.canEdit,
          view: true,
          activate: hasPermission?.canEdit,
          deactivate: hasPermission?.canEdit,
        }}

        onAction={(action, row) => {
          if (action === "edit") {
            handleAddTemplate(null, row);
          }

          if (action === "view") {
            handlePreview(
              { stopPropagation: () => { } },
              row
            );
          }

          if (action === "activate" || action === "deactivate") {
            handleToggleStatus(row.id, row.isActive);
          }
        }}
      />

      {isPreviewOpen && (
        <TermsAndConditionPreviewModal
          open={isPreviewOpen}
          handleClose={handleClosePreviewModal}
          contentData={previewContent}
        />
      )}

      {openModal && (
        <AddEditTermsConditionModal
          open={openModal}
          handleClose={handleCloseModal}
          templateDataById={ipDataById}
          setSnackbar={setSnackbar}
          getTemplateData={getWhiteListIps}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default TermsAndConditionsSection;